<template>
  <v-container fluid>
    <!-- Add loading snackbar -->
    <v-snackbar
      v-model="processSnackbar.show"
      :timeout="processSnackbar.timeout"
      :color="processSnackbar.color"
      top
      :style="{ left: '50%', transform: 'translateX(-50%)' }"
      class="notification-snackbar"
    >
      <v-alert
        :value="true"
        :color="processSnackbar.color"
        :icon="getSnackbarIcon"
        class="mb-0"
        dense
        outlined
        text
      >
        <div class="d-flex align-center">
          <v-progress-circular
            v-if="processSnackbar.loading"
            indeterminate
            size="20"
            width="2"
            :color="processSnackbar.color"
            class="mr-3"
          ></v-progress-circular>
          <span :class="`${processSnackbar.color}--text`">{{
            processSnackbar.text
          }}</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!processSnackbar.loading"
            icon
            x-small
            :color="processSnackbar.color"
            @click="processSnackbar.show = false"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-alert>
    </v-snackbar>

    <!-- Top Pagination Controls -->
    <v-row align="center" justify="space-between" class="mb-2">
      <v-col cols="12" md="6" class="d-flex align-center">
        <span class="mr-2">Rows per page:</span>
        <v-select
          :items="[30, 50, 100, 500]"
          v-model="pageSize"
          dense
          hide-details
          style="max-width: 100px"
          @change="onPageSizeChange"
        />
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <v-pagination
          v-model="page"
          :length="Math.ceil(total / pageSize)"
          :total-visible="6"
          @input="onPageChange"
        />
        <span class="ml-3">
          {{ (page - 1) * pageSize + 1 }} -
          {{ Math.min(page * pageSize, total) }}
          of {{ total }}
        </span>
      </v-col>
    </v-row>

    <!-- Filter Controls -->
    <v-card class="pa-4 mb-4 elevation-2" outlined>
      <div class="d-flex justify-end mb-2">
        <v-btn text small class="clear-btn-ux" @click="clearFilters">
          <v-icon left small color="grey">mdi-filter-remove</v-icon>
          <span class="clear-btn-label">Clear</span>
        </v-btn>
      </div>
      <v-row dense>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.insurer"
            :items="sortedInsurers"
            item-text="name"
            item-value="id"
            label="Insurer"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onFilterChange"
            prepend-icon="mdi-domain"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.branch"
            :items="sortedBranches"
            item-text="name"
            item-value="id"
            label="Branch"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onBranchChange"
            prepend-icon="mdi-office-building"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.refType"
            :items="sortedDepartments"
            item-text="name"
            item-value="id"
            label="Department"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onDeptChange"
            prepend-icon="mdi-account-group"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.subRefType"
            :items="filteredSubRefTypes"
            item-text="subCode"
            item-value="id"
            label="File Classification"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            :hint="'Select Department first to enable File Classification.'"
            :disabled="!filters.refType"
            @change="onFilterChange"
            prepend-icon="mdi-file-document-outline"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.adjuster"
            :items="filteredAdjusters"
            item-text="username"
            item-value="id"
            label="Adjuster"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            :hint="'Select Branch first to enable Adjuster.'"
            :disabled="!filters.branch"
            @change="onFilterChange"
            prepend-icon="mdi-account-tie"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.fileStatus"
            :items="stages"
            item-text="name"
            item-value="stageCode"
            label="Status"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onStatusChange"
            prepend-icon="mdi-checkbox-marked-circle-outline"
            dense
            multiple
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            clearable
            v-model="filters.vehicleNo"
            label="Vehicle No."
            @change="onFilterChange"
            prepend-icon="mdi-car"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            clearable
            v-model="filters.id"
            label="AASB Ref."
            @change="onFilterChange"
            prepend-icon="mdi-numeric"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-menu v-model="menuStart" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="filters.startDate"
              @change="
                menuStart = false;
                onFilterChange();
              "
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="2">
          <v-menu v-model="menuEnd" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.endDate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="filters.endDate"
              @change="
                menuEnd = false;
                onFilterChange();
              "
            />
          </v-menu>
        </v-col>
      <v-col cols="12" md="2" class="d-flex align-center">
          <!-- Export buttons visible only to permitted roles -->
          <v-btn
            v-if="canExport"
            color="success"
            small
            class="mr-2"
            elevation="2"
            style="min-width: 140px"
            @click="exportCurrentPage"
          >
            <v-icon left>mdi-file-excel</v-icon>
            Export Current Page
          </v-btn>
          <v-btn
            v-if="canExport"
            color="primary"
            small
            elevation="2"
            :loading="exportingAll"
            style="min-width: 140px"
            @click="exportAll"
            :disabled="false"
          >
            <v-icon left>mdi-file-export</v-icon>
            Export All (Filtered)
          </v-btn>
        </v-col>
        <!-- Remove warning for >10k rows -->
        <!-- <v-col cols="12" v-if="total > 10000">
          <v-alert type="warning" dense outlined border="left" color="orange">
            <v-icon left color="orange darken-2">mdi-alert</v-icon>
            Export is limited to 10,000 rows. Please refine your filters to
            reduce the result set.
          </v-alert>
        </v-col> -->
      </v-row>
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="dialogDetail" width="600px" persistent>
      <v-card tile>
        <v-card-actions>
          <template v-if="checkingFinalReport">
            <v-btn text small disabled class="ml-2">
              <v-progress-circular
                indeterminate
                size="20"
                width="2"
                color="primary"
                class="mr-2"
              ></v-progress-circular>
              Checking Final Reports...
            </v-btn>
          </template>
          <v-menu
            v-else-if="finalReports && finalReports.length > 0"
            offset-y
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="success"
                outlined
                small
                tile
                v-bind="attrs"
                v-on="on"
                class="ml-2"
              >
                <v-icon left>mdi-file-document-outline</v-icon>
                Final Reports ({{ finalReports.length }})
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(report, index) in finalReports"
                :key="index"
                @click="downloadFinalReport(report)"
              >
                <v-list-item-icon>
                  <v-icon small>mdi-download</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="caption">
                    {{ report.Type || "Report" }} -
                    {{ formatDate(report.createdAt) }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ report.Remark || "No remark" }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <template v-else>
            <v-btn text small disabled class="ml-2 caption">
              <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
              No Final Reports Available
            </v-btn>
          </template>
          <v-spacer></v-spacer>
          <v-btn outlined small tile @click="dialogDetail = false">
            Close
          </v-btn>
        </v-card-actions>
        <v-card-title>
          <v-spacer></v-spacer>
          <span class="caption text-decoration-overline"
            >Details of AA/<span
              v-for="(dept, index) in filterDept(caseDetail.refType)"
              :key="index"
              >{{ dept.name }}
              <span
                v-for="(subRef, index) in filterSubDept(caseDetail.subRefType)"
                :key="index"
                >/{{ subRef.subCode }}</span
              > </span
            >/{{ caseDetail.id }}/{{ yearOnly(caseDetail.createdAt) }}/<span
              v-for="branch in filterBranch(caseDetail.branch)"
              :key="branch.id"
              >{{ branch.brCode }}
            </span>
          </span>
        </v-card-title>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-center font-weight-black">Name</th>
                <th class="text-left font-weight-black">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="caseDetail.fileStatus">
                <td class="text-center">Status</td>
                <td
                  v-for="fileStatus in filterStages(caseDetail.fileStatus)"
                  :key="fileStatus.id"
                  class="text-left font-italic font-weight-black"
                >
                  {{ fileStatus.name }}
                </td>
              </tr>
              <tr v-if="caseDetail.createdAt">
                <td class="text-center">Created On:</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.createdAt) }}
                </td>
              </tr>
              <tr v-if="caseDetail.createdAt">
                <td class="text-center">Created By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.createdBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.transferDate">
                <td class="text-center">Transfer Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.transferDate) }}
                </td>
              </tr>
              <tr v-if="caseDetail.transferDate">
                <td class="text-center">Transfered By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.transferBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfCancel">
                <td class="text-center">Cancellation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfCancel) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfCancel">
                <td class="text-center">Cancellation By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.cancelBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.caseFrom">
                <td class="text-center">Type</td>
                <td class="text-left font-italic">
                  {{ caseDetail.caseFrom }}
                </td>
              </tr>

              <tr v-if="caseDetail.refType">
                <td class="text-center">Department</td>
                <td class="text-left font-italic">
                  {{ filterDept(caseDetail.refType)[0].name }}, ({{
                    filterDept(caseDetail.refType)[0].description
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.subRefType">
                <td class="text-center">File Classification</td>
                <td class="text-left font-italic">
                  {{ filterSubDept(caseDetail.subRefType)[0].subCode }}
                </td>
              </tr>
              <tr v-if="caseDetail.branch">
                <td class="text-center">Branch</td>
                <td class="text-left font-italic">
                  {{ filterBranch(caseDetail.branch)[0].brCode }}, ({{
                    filterBranch(caseDetail.branch)[0].name
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.insurer">
                <td class="text-center">Insurer</td>
                <td class="text-left font-italic">
                  {{ filterIns(caseDetail.insurer)[0].name }}
                </td>
              </tr>
              <tr v-if="caseDetail.handler">
                <td class="text-center">Handler</td>
                <td class="text-left font-italic">
                  {{
                    filterHandler(caseDetail.handler).length > 0
                      ? filterHandler(caseDetail.handler)[0].name
                      : "TBA"
                  }}, ({{
                    filterHandler(caseDetail.handler).length > 0
                      ? filterHandler(caseDetail.handler)[0].email
                      : "N/A"
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.adjuster">
                <td class="text-center">Adjuster</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.adjuster) }}
                </td>
              </tr>
              <tr v-if="caseDetail.editor">
                <td class="text-center">Editor</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.editor) }}
                </td>
              </tr>
              <tr v-if="caseDetail.clerkInCharge">
                <td class="text-center">Clerk In Charge</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.clerkInCharge) }}
                </td>
              </tr>
              <tr v-if="caseDetail.claimNo">
                <td class="text-center">Insurer's Ref No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.claimNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.vehicleNo">
                <td class="text-center">Vehicle No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.vehicleNo.toUpperCase() }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfAssign">
                <td class="text-center">Date Of Assignment</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfAssign) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfLoss">
                <td class="text-center">Date Of Loss</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfLoss) }}
                </td>
              </tr>
              <tr v-if="caseDetail.stateOfLoss">
                <td class="text-center">State Of Loss</td>
                <td class="text-left font-italic">
                  {{
                    filterState(caseDetail.stateOfLoss).length > 0
                      ? filterState(caseDetail.stateOfLoss)[0].stCode
                      : "TBA"
                  }}, ({{
                    filterState(caseDetail.stateOfLoss).length > 0
                      ? filterState(caseDetail.stateOfLoss)[0].name
                      : "N/A"
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.placeOfLoss">
                <td class="text-center">Place Of Loss</td>
                <td class="text-left font-italic">
                  {{ caseDetail.placeOfLoss }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredName">
                <td class="text-center">Insured Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredName }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredIC">
                <td class="text-center">Insured IC No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredIC }}
                </td>
              </tr>
              <tr v-if="caseDetail.InsuredTel">
                <td class="text-center">Insured Tel. No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.InsuredTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredEmail">
                <td class="text-center">Insured Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredDriverName">
                <td class="text-center">Insured Driver Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredDriverName }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredDriverIc">
                <td class="text-center">Insured Driver IC No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredDriverIc }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredDriverEmail">
                <td class="text-center">Insured Driver Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredDriverEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.policeReportNo">
                <td class="text-center">Police Report No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.policeReportNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.policeReportDate">
                <td class="text-center">Police Report Date</td>
                <td class="text-left font-italic">
                  {{ caseDetail.policeReportDate }}
                </td>
              </tr>
              <tr v-if="caseDetail.policyNo">
                <td class="text-center">Policy Number</td>
                <td class="text-left font-italic">
                  {{ caseDetail.policyNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.coverFrom || caseDetail.coverTo">
                <td class="text-center">Cover Period</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.coverFrom) }} -
                  {{ formatDate(caseDetail.coverTo) }}
                </td>
              </tr>
              <tr v-if="caseDetail.insComment">
                <td class="text-center">Insurer's Comment</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insComment }}
                </td>
              </tr>
              <tr v-if="caseDetail.solicitorName">
                <td class="text-center">Solicitor Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.solicitorName }}
                </td>
              </tr>
              <tr v-if="caseDetail.solicitorTel">
                <td class="text-center">Solicitor Tel.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.solicitorTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.solicitorEmail">
                <td class="text-center">Solicitor Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.solicitorEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.counselName">
                <td class="text-center">Counsel Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.counselName }}
                </td>
              </tr>
              <tr v-if="caseDetail.counselTel">
                <td class="text-center">Counsel Tel</td>
                <td class="text-left font-italic">
                  {{ caseDetail.counselTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.counselEmail">
                <td class="text-center">Counsel Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.counselEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantName">
                <td class="text-center">Third Party Claimant Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantName }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantIc">
                <td class="text-center">Third Party Claimant IC No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantIc }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantTel">
                <td class="text-center">Third Party Claimant Tel No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantEmail">
                <td class="text-center">Third Party Claimant Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleNo">
                <td class="text-center">Third Party Claimant Vehicle No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleModel">
                <td class="text-center">Third Party Claimant Vehicle Model</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleModel }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleSolicitor">
                <td class="text-center">Third Party Claimant Solicitor</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleSolicitor }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleSolicitorEmail">
                <td class="text-center">
                  Third Party Claimant Solicitor Email
                </td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleSolicitorEmail }}
                </td>
              </tr>

              <tr v-if="caseDetail.applyDocDate">
                <td class="text-center">Document Application Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.applyDocDate) }}
                </td>
              </tr>
              <tr v-if="caseDetail.applyDocDate">
                <td class="text-center">Document Application By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.applyDocBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfReg">
                <td class="text-center">Branch Assigning Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfReg) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfReg">
                <td class="text-center">Branch Assigned By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignBranchBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfAdj">
                <td class="text-center">Adjuster Assigning Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfAdj) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfAdj">
                <td class="text-center">Adjuster Assigning By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignAdjBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateStartInv">
                <td class="text-center">Start Investigation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateStartInv) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateEndInv">
                <td class="text-center">Complete Investigation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateEndInv) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfEdi">
                <td class="text-center">Editor Assigned Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfEdi) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfEdi">
                <td class="text-center">Editor Assigned By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignEdiBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateStartEdi">
                <td class="text-center">Start Editing Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateStartEdi) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateEndEdi">
                <td class="text-center">Complete Editing Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateEndEdi) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfApproval">
                <td class="text-center">Approval Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfApproval) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfApproval">
                <td class="text-center">Approval By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.approvedBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateAssignFormatting">
                <td class="text-center">Clerk Assigned Date(Formatting)</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateAssignFormatting) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateAssignFormatting">
                <td class="text-center">Clerk Assigned (Formatting) By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignClerkBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateStartFormatting">
                <td class="text-center">Start Formatting Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateStartFormatting) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateEndFormatting">
                <td class="text-center">Complete Formatting Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateEndFormatting) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoising & Finalisation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateFinal) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoising & Finalisation By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.finalBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoice Nombor</td>
                <td class="text-left font-italic">
                  {{ caseDetail.invNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoice Fee Note</td>
                <td class="text-left font-italic">
                  {{ caseDetail.invFeeNote }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoice Total</td>
                <td class="text-left font-italic">
                  RM {{ caseDetail.invTotal }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateDispatch">
                <td class="text-center">Dispatch Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateDispatch) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateDispatch">
                <td class="text-center">Dispatch By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.dispatchBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateClosed">
                <td class="text-center">Closed Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateClosed) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateClosed">
                <td class="text-center">Closed By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.closedBy) }}
                </td>
              </tr>

              <!-- <tr v-for="(item, index) in caseDetail" :key="index">
                <td>{{ index }}</td>
                <td>{{ item }}</td>
              </tr> -->
            </tbody>
          </template>
        </v-simple-table>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined small tile @click="dialogDetail = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="roleFilteredCasefiles"
      :loading="loading"
      :page.sync="page"
      :items-per-page.sync="pageSize"
      :server-items-length="total"
      class="elevation-1"
      @update:page="onPageChange"
      @update:items-per-page="onPageSizeChange"
      dense
      style="background: #fff; border-radius: 8px"
      @click:row="showDetail"
      hide-default-footer
    >
      <!-- Numbering -->
      <template v-slot:[`item.numbering`]="{ index }">
        {{ (page - 1) * pageSize + index + 1 }}
      </template>
      <!-- Insurer -->
      <template v-slot:[`item.insurer`]="{ item }">
        <span>{{ getInsurerName(item.insurer) }}</span>
      </template>
      <!-- Branch -->
      <template v-slot:[`item.branch`]="{ item }">
        <span>{{ getBranchName(item.branch) }}</span>
      </template>
      <!-- Department -->
      <template v-slot:[`item.refType`]="{ item }">
        <span>{{ getDeptName(item.refType) }}</span>
      </template>
      <!-- File Classification -->
      <template v-slot:[`item.subRefType`]="{ item }">
        <span>{{ getSubRefTypeName(item.subRefType) }}</span>
      </template>
      <!-- Adjuster -->
      <template v-slot:[`item.adjuster`]="{ item }">
        <span>{{ getAdjusterName(item.adjuster) }}</span>
      </template>
      <!-- Vehicle No (uppercase) -->
      <template v-slot:[`item.vehicleNo`]="{ item }">
        <span>{{ item.vehicleNo ? item.vehicleNo.toUpperCase() : "" }}</span>
      </template>
      <!-- AASB Reference -->
      <template v-slot:[`item.id`]="{ item }">
        AA/
        <span
          v-for="(dept, deptIdx) in getDept(item.refType)"
          :key="`dept-${dept.id}-${item.id}-${deptIdx}`"
          >{{ dept.name }}</span
        >
        <span
          v-for="(sub, subIdx) in getSubRefType(item.subRefType)"
          :key="`sub-${sub.id}-${item.id}-${subIdx}`"
          >/{{ sub.subCode }}</span
        >
        /{{ item.id }}/{{ yearOnly(item.createdAt) }}
        <span
          v-for="(branch, branchIdx) in getBranch(item.branch)"
          :key="`branch-${branch.id}-${item.id}-${branchIdx}`"
          >/{{ branch.brCode }}</span
        >
      </template>
      <!-- Date Of Assignment -->
      <template v-slot:[`item.dateOfAssign`]="{ item }">
        <span>{{ formatDate(item.dateOfAssign) }}</span>
      </template>
      <!-- Days -->
      <template v-slot:[`item.days`]="{ item }">
        <span>{{ item.days }}</span>
      </template>
      <!-- Status -->
      <template v-slot:[`item.fileStatus`]="{ item }">
        <span>{{ getFileStatusName(item.fileStatus) }}</span>
      </template>
      <!-- ...other custom columns as needed... -->
    </v-data-table>

    <!-- Bottom Pagination Controls -->
    <v-row align="center" justify="space-between" class="mt-2">
      <v-col cols="12" md="6" class="d-flex align-center">
        <span class="mr-2">Rows per page:</span>
        <v-select
          :items="[30, 50, 100, 500]"
          v-model="pageSize"
          dense
          hide-details
          style="max-width: 100px"
          @change="onPageSizeChange"
        />
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <v-pagination
          v-model="page"
          :length="Math.ceil(total / pageSize)"
          :total-visible="6"
          @input="onPageChange"
        />
        <span class="ml-3">
          {{ (page - 1) * pageSize + 1 }} -
          {{ Math.min(page * pageSize, total) }}
          of {{ total }}
        </span>
      </v-col>
    </v-row>

    <!-- Export Progress Overlay -->
    <v-dialog v-model="exportingAll" persistent max-width="420px" overlay>
      <v-card>
        <v-card-title class="headline">Exporting Excel...</v-card-title>
        <v-card-text>
          <div class="d-flex align-center">
            <v-progress-circular
              v-if="exportProgress === null && !exportError"
              indeterminate
              color="primary"
              size="40"
            ></v-progress-circular>

            <v-progress-circular
              v-else-if="typeof exportProgress === 'number' && !exportError"
              :value="exportProgress"
              color="primary"
              size="40"
              width="4"
            >
              {{ exportProgress }}%
            </v-progress-circular>

            <v-icon v-else-if="exportError" color="error" size="40">mdi-alert-circle</v-icon>

            <div class="ml-4">
              <div>{{ exportStatus || 'Preparing your Excel file...' }}</div>
              <div class="mt-2 grey--text text--darken-1" style="font-size: 13px">
                Elapsed: {{ exportAllSeconds }} second{{ exportAllSeconds === 1 ? '' : 's' }}
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            v-if="exportError"
            @click="exportAll"
          >
            Retry
          </v-btn>
          <v-btn color="primary" text @click="closeExportAll"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import moment from "moment";
import BranchDataService from "../../services/BranchDataService";
import DeptDataService from "../../services/DeptDataService";
import SubRefTypeDataService from "../../services/SubDeptDataService";
import InsDataService from "../../services/InsDataService";
import MemberDataService from "../../services/MemberDataService";
import HandlerDataService from "../../services/HandlerDataService";
import StateDataService from "../../services/StateDataService";
import StagesDataService from "../../services/StageDataService";
import { apiConfig } from "../../config/api.config";
import DocsDataService from "../../services/DocsDataService";
import axios from "axios";
import TokenService from "../../services/token.service";

export default {
  name: "reportsV1",
  data() {
    return {
      filters: {
        insurer: "",
        branch: "",
        refType: "",
        subRefType: "",
        adjuster: "",
        claimNo: "",
        vehicleNo: "",
        startDate: null,
        endDate: null,
      },
      page: 1,
      pageSize: 30,
      menuStart: false,
      menuEnd: false,
      branches: [],
      departments: [],
      subRefTypes: [],
      insurers: [],
      adjusters: [],
      stages: [],
      allFilteredCasefiles: [],
      exportingAll: false,
      dialogDetail: false,
      caseDetail: {},
      finalReports: [],
      checkingFinalReport: false,
      processSnackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
        loading: false,
      },
      exportAllIframe: null, // For canceling export
      exportStatus: "",
      exportProgress: null,
      exportError: false,
      exportAllSeconds: 0,
      exportAllTimer: null,
    };
  },
    computed: {
    ...mapGetters([
      "getPaginatedAll",
      "getPaginatedTotalAll",
      // Add this getter in your store if not present:
      // getPaginatedAllLoading: (state) => state.casefiles.paginatedAll.isLoading,
    ]),
    ...mapState({
      user: (state) => state.auth.user,
    }),
    paginatedCasefiles() {
      // Use paginatedAll data from store
      return this.getPaginatedAll;
    },
    // Add role-based filtering for displayed casefiles
    roleFilteredCasefiles() {
      // No extra filtering, just return paginatedCasefiles
      return this.paginatedCasefiles;
    },
    total() {
      return this.getPaginatedTotalAll;
    },
    loading() {
      // Use paginatedAll loading state if you add the getter
      return this.$store.getters.getPaginatedAllLoading || false;
    },
    sortedInsurers() {
      return [...this.insurers]
        .map((ins) => ({
          ...ins,
          name: ins.name ? ins.name.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    sortedDepartments() {
      return [...this.departments]
        .map((dept) => ({
          ...dept,
          name: dept.name ? dept.name.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    sortedBranches() {
      return [...this.branches]
        .map((b) => ({
          ...b,
          name: b.name ? b.name.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    sortedAdjusters() {
      return [...this.adjusters].sort((a, b) =>
        a.username.localeCompare(b.username)
      );
    },
    filteredSubRefTypes() {
      if (!this.filters.refType) return [];
      // Filter and sort file classification by subCode
      return this.subRefTypes
        .filter(
          (sub) =>
            sub.deptId === this.filters.refType ||
            sub.deptId === Number(this.filters.refType) ||
            sub.deptId === String(this.filters.refType)
        )
        .sort((a, b) => a.subCode.localeCompare(b.subCode));
    },
    filteredAdjusters() {
      if (!this.filters.branch) return [];
      const branchObj = this.branches.find(
        (b) =>
          b.id === this.filters.branch ||
          b.id === Number(this.filters.branch) ||
          b.id === String(this.filters.branch)
      );
      if (!branchObj) return [];
      const branchName = branchObj.name;
      // Only include users with "adjuster" role and active === true
      return this.adjusters
        .filter(
          (adj) =>
            adj.active === true &&
            Array.isArray(adj.roles) &&
            adj.roles.some((r) => r.roleCode === "adjuster") &&
            Array.isArray(adj.branches) &&
            adj.branches.some((b) => b.name === branchName)
        )
        .map((adj) => ({
          ...adj,
          username: adj.username ? adj.username.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.username.trim().localeCompare(b.username.trim()));
    },
    headers() {
      return [
        { text: "NO.", value: "numbering" },
        { text: "INSURER", value: "insurer" },
        { text: "BRANCH", value: "branch" },
        { text: "DEPARTMENT", value: "refType" },
        { text: "FILE CLASSIFICATION", value: "subRefType" },
        { text: "ADJUSTER", value: "adjuster" },
        { text: "VEHICLE NO.", value: "vehicleNo" },
        { text: "AASB REFERENCE", value: "id" },
        { text: "DATE OF ASSIGNMENT", value: "dateOfAssign" },
        { text: "DAYS", value: "days" },
        { text: "STATUS", value: "fileStatus" },
      ];
    },
    excelColumns() {
      return [
        { label: "CLAIMS HANDLER", field: "handler" },
        { label: "INSURER'S REF", field: "claimNo" },
        { label: "INSURED NAME", field: "insuredName" },
        { label: "VEHICLE NO", field: "vehicleNo" },
        { label: "INSURER", field: "insurer" },
        { label: "CLAIM TYPE", field: "claimType" },
        { label: "BRANCH", field: "branch" },
        { label: "DEPARTMENT", field: "department" },
        { label: "FILE CLASSIFICATION", field: "fileClassification" },
        { label: "ADJUSTER", field: "adjuster" },
        { label: "AASB REF", field: "aasbRef" },
        { label: "DATE OF ASSIGNMENT", field: "dateOfAssign" },
        { label: "DATE OF LOSS", field: "dateOfLoss" },
        { label: "DAYS", field: "days" },
        { label: "ADJUSTER ACKNOWLEDGEMENT DATE", field: "createdAt" },
        { label: "REPORT SUBMISSION DATE", field: "dateEndInv" },
        { label: "STATUS", field: "fileStatus" },
        { label: "TOTAL AMOUNT OF ALL TAX INVOICES", field: "invTotal" },
      ];
    },
    excelFormattedData() {
      return this.paginatedCasefiles.map((item) => ({
        handler: this.getHandlerName?.(item.handler) || "",
        claimNo: item.claimNo || "",
        insuredName: item.insuredName || "",
        vehicleNo: item.vehicleNo ? item.vehicleNo.toUpperCase() : "",
        insurer: this.getInsurerName(item.insurer),
        claimType: this.getDeptName(item.refType),
        branch: this.getBranchName(item.branch),
        department: this.getDeptName(item.refType),
        fileClassification: this.getSubRefTypeName(item.subRefType),
        adjuster: this.getAdjusterName(item.adjuster),
        aasbRef: `AA/${this.getDeptName(item.refType)}/${this.getSubRefTypeName(
          item.subRefType
        )}/${item.id}/${this.yearOnly(item.createdAt)}/${
          this.getBranch(item.branch)[0]?.brCode || ""
        }`,
        dateOfAssign: this.formatDate(item.dateOfAssign),
        dateOfLoss: this.formatDate(item.dateOfLoss),
        days: item.days,
        createdAt: this.formatDate(item.createdAt),
        dateEndInv: this.formatDate(item.dateEndInv),
        fileStatus: this.getFileStatusName(item.fileStatus),
        invTotal: item.invTotal || "",
      }));
    },
    excelFileName() {
      // Add this computed property to fix the error
      return `Report_${moment().format("YYYYMMDD")}`;
    },
    getSnackbarIcon() {
      switch (this.processSnackbar.color) {
        case "success":
          return "mdi-check-circle";
        case "error":
          return "mdi-alert-circle";
        case "warning":
          return "mdi-alert";
        default:
          return "mdi-information";
      }
    },
    // Permission: canExport used to control export button visibility
    // Rule: users whose roles are only ADJUSTER or EDITOR should NOT see export buttons.
    // All other roles may view the export buttons. If a user has multiple roles and at
    // least one role is not ADJUSTER/EDITOR then exports are allowed.
    canExport() {
      const user = this.user || {};
      if (!user || !user.roles) return false;

      const roleCodes = Array.isArray(user.roles)
        ? user.roles
            .map((r) => (typeof r === "string" ? r.toUpperCase() : (r.roleCode || "").toUpperCase()))
            .filter((r) => r)
        : [];

      if (roleCodes.length === 0) return false;

      // If every role is ADJUSTER or EDITOR, deny export
      const excluded = ["ADJUSTER", "EDITOR"];
      const onlyExcluded = roleCodes.every((rc) => excluded.includes(rc));
      return !onlyExcluded;
    },
  },
  methods: {
    async fetchPaginated() {
      const filters = {};
      Object.keys(this.filters).forEach((key) => {
        if (key === "fileStatus" && this.filters.fileStatus === "all") {
          return;
        }
        if (
          this.filters[key] !== null &&
          this.filters[key] !== "" &&
          typeof this.filters[key] !== "undefined"
        ) {
          filters[key] = this.filters[key];
        }
      });
      if (
        filters.adjuster &&
        typeof filters.adjuster === "object" &&
        filters.adjuster.id
      ) {
        filters.adjuster = filters.adjuster.id;
      }
      if (filters.id) {
        filters.id = Number(filters.id);
      }
      // Use fetchPaginatedAll for unfiltered/all files
      await this.$store.dispatch("casefiles/fetchPaginatedAll", {
        page: this.page,
        pageSize: this.pageSize,
        filters,
      });
    },
    onPageChange(page) {
      this.page = page;
      this.fetchPaginated();
    },
    onPageSizeChange() {
      this.page = 1;
      this.fetchPaginated();
    },
    onFilterChange() {
      // If "All Statuses" selected, clear fileStatus filter
      if (this.filters.fileStatus === "all") {
        this.filters.fileStatus = "";
      }
      this.page = 1;
      this.fetchPaginated();
    },
    onBranchChange() {
      this.filters.adjuster = "";
      this.onFilterChange();
    },
    onDeptChange() {
      this.filters.subRefType = "";
      this.onFilterChange();
    },
    onStatusChange(value) {
      // If all statuses are selected, clear the filter (show all)
      if (Array.isArray(value) && value.length === this.stages.length) {
        this.filters.fileStatus = [];
      } else {
        this.filters.fileStatus = value;
      }
      this.onFilterChange();
    },
    formatDate(value) {
      if (!value) return "";
      return moment(value).format("DD/MM/YYYY");
    },
    yearOnly(value) {
      return moment(value).format("YY");
    },
    getInsurerName(id) {
      const ins = this.insurers.find((i) => i.id === id);
      return ins ? ins.name : "";
    },
    getBranchName(id) {
      const br = this.branches.find((b) => b.id === id);
      return br ? br.name : "";
    },
    getDeptName(id) {
      const dept = this.departments.find((d) => d.id === id);
      return dept ? dept.name : "";
    },
    getSubRefTypeName(id) {
      const sub = this.subRefTypes.find((s) => s.id === id);
      return sub ? sub.subCode : "";
    },
    getAdjusterName(id) {
      // Reference logic from reportsTwo.vue's filterAdj
      if (!id) return "TBA";
      const filtered = this.adjusters.filter((adjuster) => adjuster.id === id);
      return filtered.length ? filtered[0].username : "TBA";
    },
    getFileStatusName(code) {
      const stage = this.stages.find((s) => s.stageCode === code);
      return stage ? stage.name : code;
    },
    getDept(id) {
      return this.departments.filter((d) => d.id === id);
    },
    getSubRefType(id) {
      return this.subRefTypes.filter((s) => s.id === id);
    },
    getBranch(id) {
      return this.branches.filter((b) => b.id === id);
    },
    async loadMeta() {
      this.branches = (await BranchDataService.getAll()).data;
      this.departments = (await DeptDataService.getAll()).data;
      this.subRefTypes = (await SubRefTypeDataService.getAll()).data;
      this.insurers = (await InsDataService.getAll()).data;
      this.adjusters = (await MemberDataService.getAll()).data.filter(
        (item) =>
          item.roles && item.roles.some((r) => r.roleCode === "adjuster")
      );
      this.stages = (await StagesDataService.getAll()).data;
      this.members = (await MemberDataService.getAll()).data;
      this.handlers = (await HandlerDataService.getAll()).data;
      this.states = (await StateDataService.getAll()).data;
    },
    async exportAll() {
      // initialize export UI state
      this.exportingAll = true;
      this.exportStatus = "Preparing your Excel file...";
      this.exportProgress = null;
      this.exportError = false;
      this.exportAllSeconds = 0;
      if (this.exportAllTimer) clearInterval(this.exportAllTimer);
      this.exportAllTimer = setInterval(() => {
        this.exportAllSeconds++;
      }, 1000);

      const filters = {};
      Object.keys(this.filters).forEach((key) => {
        if (
          this.filters[key] !== null &&
          this.filters[key] !== "" &&
          typeof this.filters[key] !== "undefined"
        ) {
          filters[key] = this.filters[key];
        }
      });
      if (
        filters.adjuster &&
        typeof filters.adjuster === "object" &&
        filters.adjuster.id
      ) {
        filters.adjuster = filters.adjuster.id;
      }
      if (filters.id) {
        filters.id = Number(filters.id);
      }
      if (Array.isArray(filters.fileStatus)) {
        filters.fileStatus = JSON.stringify(filters.fileStatus);
      }
      let baseURL = this.$apiConfig?.baseURL || apiConfig.baseURL;
      if (baseURL.endsWith("/api")) {
        baseURL = baseURL.replace(/\/api$/, "");
      }
      try {
        const params = new URLSearchParams({
          ...filters,
        }).toString();

        const url = `${baseURL}/api/casefiles/export-all?${params}`;
        const token = TokenService.getLocalAccessToken();

        const axiosConfig = {
          method: "get",
          url,
          responseType: "blob",
          headers: {},
          onDownloadProgress: (progressEvent) => {
            try {
              if (progressEvent.lengthComputable) {
                this.exportProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                this.exportStatus = `Downloading... (${this.exportProgress}%)`;
              } else {
                this.exportStatus = "Downloading...";
              }
            } catch (e) {
              // ignore progress errors
            }
          },
        };
        if (token) axiosConfig.headers["x-access-token"] = token;

        const response = await axios(axiosConfig);

        // success: create blob and trigger download
        const contentDisposition = response.headers["content-disposition"] || "";
        let filename = "";
        const filenameMatch = /filename\*=UTF-8''([^;\n]*)/.exec(contentDisposition);
        if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1].replace(/"/g, ""));
        } else {
          const fallbackMatch = /filename="?([^";]*)"?/.exec(contentDisposition);
          if (fallbackMatch && fallbackMatch[1]) filename = fallbackMatch[1];
        }
        if (!filename) filename = `${this.excelFileName}.xlsx`;

        const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" });
        const fileURL = window.URL.createObjectURL(blob);
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute("download", filename);
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);

        this.exportStatus = "Download complete. Closing...";
        // give user a brief moment to see success, then close
        setTimeout(() => {
          this.closeExportAll();
        }, 800);
      } catch (error) {
        console.error("Export All error:", error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.exportStatus = "You are not authenticated. Please log in.";
        } else {
          this.exportStatus = "Download failed. Please try again or contact support.";
        }
        this.exportError = true;
        if (this.exportAllTimer) {
          clearInterval(this.exportAllTimer);
          this.exportAllTimer = null;
        }
      }
    },
    closeExportAll() {
      this.exportingAll = false;
      if (this.exportAllIframe) {
        document.body.removeChild(this.exportAllIframe);
        this.exportAllIframe = null;
      }
      if (this.exportAllTimer) {
        clearInterval(this.exportAllTimer);
        this.exportAllTimer = null;
      }
      this.exportAllSeconds = 0;
      this.exportStatus = "";
      this.exportProgress = null;
      this.exportError = false;
    },
    async exportCurrentPage() {
      // Use axios/blob download for current page so we can attach auth and show progress
      this.exportingAll = true;
      this.exportStatus = "Preparing your Excel file...";
      this.exportProgress = null;
      this.exportError = false;
      this.exportAllSeconds = 0;
      if (this.exportAllTimer) clearInterval(this.exportAllTimer);
      this.exportAllTimer = setInterval(() => {
        this.exportAllSeconds++;
      }, 1000);

      let baseURL = apiConfig.baseURL;
      if (baseURL.endsWith("/api")) {
        baseURL = baseURL.replace(/\/api$/, "");
      }
      const filters = {};
      Object.keys(this.filters).forEach((key) => {
        if (
          this.filters[key] !== null &&
          this.filters[key] !== "" &&
          typeof this.filters[key] !== "undefined"
        ) {
          filters[key] = this.filters[key];
        }
      });
      if (
        filters.adjuster &&
        typeof filters.adjuster === "object" &&
        filters.adjuster.id
      ) {
        filters.adjuster = filters.adjuster.id;
      }
      if (filters.id) {
        filters.id = Number(filters.id);
      }
      if (Array.isArray(filters.fileStatus)) {
        filters.fileStatus = JSON.stringify(filters.fileStatus);
      }

      const params = new URLSearchParams({
        ...filters,
        page: this.page,
        pageSize: this.pageSize,
        export: "excel",
      }).toString();

      const url = `${baseURL}/api/casefiles/paginated?${params}`;
      const token = TokenService.getLocalAccessToken();

      const axiosConfig = {
        method: "get",
        url,
        responseType: "blob",
        headers: {},
        onDownloadProgress: (progressEvent) => {
          try {
            if (progressEvent.lengthComputable) {
              this.exportProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              this.exportStatus = `Downloading... (${this.exportProgress}%)`;
            } else {
              this.exportStatus = "Downloading...";
            }
          } catch (e) {
            // ignore
          }
        },
      };
      if (token) axiosConfig.headers["x-access-token"] = token;

      try {
        const response = await axios(axiosConfig);

        const contentDisposition = response.headers["content-disposition"] || "";
        let filename = "";
        const filenameMatch = /filename\*=UTF-8''([^;\n]*)/.exec(contentDisposition);
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1].replace(/"/g, ""));
        } else {
          const fallbackMatch = /filename="?([^";]*)"?/.exec(contentDisposition);
          if (fallbackMatch && fallbackMatch[1]) filename = fallbackMatch[1];
        }
        if (!filename) filename = `${this.excelFileName}.xlsx`;

        const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" });
        const fileURL = window.URL.createObjectURL(blob);
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute("download", filename);
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);

        this.exportStatus = "Download complete. Closing...";
        setTimeout(() => this.closeExportAll(), 800);
      } catch (error) {
        console.error("Export current page error:", error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.exportStatus = "You are not authenticated. Please log in.";
        } else {
          this.exportStatus = "Download failed. Please try again or contact support.";
        }
        this.exportError = true;
        if (this.exportAllTimer) {
          clearInterval(this.exportAllTimer);
          this.exportAllTimer = null;
        }
      }
    },
    clearAllFilteredCasefiles() {
      this.allFilteredCasefiles = [];
    },
    async showDetail(item) {
      this.caseDetail = Object.assign({}, item);
      this.dialogDetail = true;
      this.finalReports = [];
      this.checkingFinalReport = true;

      try {
        // Fetch documents for this specific case only (final reports check)
        // Request server-filtered final reports for this case to avoid
        // client-side pagination/limit issues. Include a generous limit.
        const [docsResponse] = await Promise.all([
          DocsDataService.findByCase(item.id, { docStageId: 4, limit: 1000 }),
          new Promise((resolve) => setTimeout(resolve, 500)),
        ]);

        // Normalize response: DocsDataService.getAll may return either an array
        // or an object { data: rows, totalCount } depending on API.
        let docs = [];
        if (docsResponse && docsResponse.data) {
          if (Array.isArray(docsResponse.data)) docs = docsResponse.data;
          else if (Array.isArray(docsResponse.data.data)) docs = docsResponse.data.data;
          else if (Array.isArray(docsResponse.data.rows)) docs = docsResponse.data.rows;
          else docs = [];
        }

        this.documents = docs;
        // Server should have already filtered by docStageId=4, but keep a
        // defensive client-side filter to be safe.
        this.finalReports = this.documents.filter(
          (doc) => doc.caseId === item.id && doc.docStageId === 4
        );

        // (Removed debug logging for production readiness)

        // Optional: Show notification when check is complete
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: this.finalReports.length ? "success" : "info",
          text: this.finalReports.length
            ? `${this.finalReports.length} final report(s) available`
            : "No final reports found for this case",
        });
      } catch (e) {
        console.error("Error fetching documents:", e);
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Error checking for final reports",
        });
      } finally {
        this.checkingFinalReport = false;
      }
    },
    downloadFinalReport(report) {
      if (!report) return;

      axios({
        url: `${apiConfig.docsURL}/${report.name}`,
        method: "GET",
        responseType: "blob",
      })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;

          const ext = report.name.substr(report.name.lastIndexOf(".") + 1);
          const caseVehicleNo = this.caseDetail.vehicleNo
            ? this.caseDetail.vehicleNo.toUpperCase()
            : "VEHICLE";
          const timestamp = moment(report.createdAt).format("YYYYMMDD");

          fileLink.setAttribute(
            "download",
            `${caseVehicleNo}_${this.caseDetail.id}_${timestamp}.${ext}`
          );

          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
        })
        .catch((error) => {
          console.error("Error downloading file:", error);
          this.$store.dispatch("setSnackbar", {
            showing: true,
            color: "error",
            text: "Error downloading final report",
          });
        });
    },
    // Add missing helpers for dialog detail
    filterDept(item) {
      if (!item) return [];
      return this.departments.filter((dept) => dept.id === item);
    },
    filterSubDept(item) {
      if (!item) return [];
      return this.subRefTypes.filter((subDept) => subDept.id === item);
    },
    filterBranch(item) {
      if (!item) return [];
      return this.branches.filter((branch) => branch.id === item);
    },
    filterStages(item) {
      if (!item) return [];
      return this.stages.filter((stage) => stage.stageCode === item);
    },
    filterUser(item) {
      if (!this.members || !item) return "";
      const found = this.members.filter((member) => member.id === item);
      return found.length && found[0] && found[0].username
        ? `${found[0].username}, ${found[0].email || ""}`
        : "";
    },
    filterIns(item) {
      if (!item) return [];
      return this.insurers.filter((insurer) => insurer.id === item);
    },
    filterState(item) {
      if (!item || !this.states) return [];
      return this.states.filter((state) => state.id === item);
    },
    filterHandler(item) {
      if (!item || !this.handlers) return [];
      const found = this.handlers.filter((handler) => handler.id === item);
      return found.length ? found : [];
    },
    showProcessUpdate(text, isLoading = true, color = "info") {
      this.processSnackbar = {
        show: true,
        text,
        color,
        timeout: isLoading ? -1 : 3000,
        loading: isLoading,
      };
    },
    async clearFilters() {
      this.showProcessUpdate("Reloading data...", true, "info");
      this.filters = {
        insurer: "",
        branch: "",
        refType: "",
        subRefType: "",
        adjuster: "",
        claimNo: "",
        vehicleNo: "",
        startDate: null,
        endDate: null,
      };
      this.page = 1;
      await this.fetchPaginated();
      this.showProcessUpdate("Data reloaded", false, "success");
    },
  },
  mounted() {
    this.loadMeta();
    this.fetchPaginated();
  },
  beforeDestroy() {
    if (this.exportAllTimer) {
      clearInterval(this.exportAllTimer);
      this.exportAllTimer = null;
    }
  },
};
</script>

<style>
/* Add pointer cursor for table rows on hover */
.v-data-table tbody tr:hover {
  cursor: pointer;
}
.clear-btn-ux {
  background-color: #f5f5f5 !important;
  color: #757575 !important;
  font-style: italic;
  font-size: 13px;
  min-width: 70px;
  box-shadow: none !important;
  border-radius: 16px;
  transition: background 0.2s;
}
.clear-btn-ux:hover {
  background-color: #eeeeee !important;
}
.clear-btn-label {
  font-style: italic;
  color: #757575;
}
.notification-snackbar {
  z-index: 2000;
}
</style>
